import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useTrackRoute } from '@/lib/analytics';
import { Button } from '@/components/ui/button';
import { 
  LayoutDashboard, 
  TestTubes, 
  Calculator, 
  Calendar, 
  Package, 
  FileText, 
  Layers, 
  Store, 
  BookOpen, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  Sun,
  Moon,
  Crown,
  TrendingUp,
  HelpCircle,
  Brain,
  Users,
  FolderOpen,
  Shield,
  Trophy
} from 'lucide-react';
import AppTutorial from '@/components/AppTutorial';
import MobileNav from './MobileNav';
import SyncIndicator from '@/components/SyncIndicator';
import { API } from '@/config/api';

export default function MainLayout({ user }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [theme, setTheme] = useState('dark');
  const [runTutorial, setRunTutorial] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if tutorial should run on first visit (desktop only — joyride targets
  // are inside the desktop sidebar which is hidden on mobile, so running on
  // mobile would trap users behind an overlay with no working Skip button)
  useEffect(() => {
    const tutorialCompleted = localStorage.getItem('peptideforge_tutorial_completed');
    const isDesktop = typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches;
    if (!tutorialCompleted && isDesktop && location.pathname === '/dashboard') {
      // Small delay to ensure DOM elements are rendered
      const timer = setTimeout(() => setRunTutorial(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  const handleTutorialComplete = () => {
    setRunTutorial(false);
  };

  const startTutorial = () => {
    localStorage.removeItem('peptideforge_tutorial_completed');
    setRunTutorial(true);
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/peptides', label: 'Peptides', icon: TestTubes },
    { path: '/calculator', label: 'Calculator', icon: Calculator },
    { path: '/scheduler', label: 'Scheduler', icon: Calendar },
    { path: '/inventory', label: 'Inventory', icon: Package },
    { path: '/logs', label: 'Logs', icon: FileText },
    { path: '/protocols', label: 'Protocols', icon: Layers },
    { path: '/collections', label: 'Collections', icon: FolderOpen },
    { path: '/marketplace', label: 'Marketplace', icon: Store },
    { path: '/forums', label: 'Community', icon: Users },
    { path: '/leaderboard', label: 'Leaderboard', icon: Trophy },
    { path: '/neuro', label: 'Neuro-Optimization', icon: Brain },
    { path: '/analytics', label: 'Analytics', icon: TrendingUp, premium: true },
    { path: '/ai-optimize', label: 'AI Optimize', icon: Brain, premium: true },
    { path: '/suppliers', label: 'Suppliers', icon: Store },
    { path: '/education', label: 'Education', icon: BookOpen },
    { path: '/settings', label: 'Settings', icon: Settings },
  ];

  const isPremium = user?.subscription_status && user.subscription_status !== 'free';
  const isAdmin = user?.role === 'admin';

  // Fire page_view analytics events on every route change
  useTrackRoute();

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      navigate('/');
    }
  };

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  return (
    <div className="flex h-screen min-h-dvh max-h-dvh bg-background overflow-hidden">
      {/* Tutorial Component */}
      <AppTutorial run={runTutorial} onComplete={handleTutorialComplete} />

      {/* Sidebar - Desktop */}
      <aside className="hidden md:flex md:flex-col w-64 border-r border-border bg-card" data-testid="desktop-sidebar">
        <div className="p-6 border-b border-border">
          <Link to="/dashboard" className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <TestTubes className="h-6 w-6 text-primary" />
            </div>
            <h1 className="text-xl font-bold tracking-tight">PeptideForge</h1>
          </Link>
          <div className="mt-3 flex">
            <SyncIndicator />
          </div>
        </div>

        <nav className="flex-1 min-h-0 p-4 space-y-1 overflow-y-auto overscroll-contain">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center justify-between space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <div className="flex items-center space-x-3">
                  <Icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </div>
                {item.premium && !isPremium && (
                  <Crown className="h-4 w-4 text-primary" />
                )}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-border space-y-2">
          {isAdmin && (
            <Link to="/admin">
              <Button
                variant="outline"
                className="w-full justify-start border-amber-500/50 hover:bg-amber-500/10 mb-2"
                data-testid="admin-panel-button"
              >
                <Shield className="h-4 w-4 mr-2 text-amber-400" />
                Admin Panel
              </Button>
            </Link>
          )}
          {!isPremium && (
            <Link to="/subscription">
              <Button
                variant="outline"
                className="w-full justify-start border-primary/50 hover:bg-primary/10 mb-2"
                data-testid="upgrade-premium-button"
              >
                <Crown className="h-4 w-4 mr-2 text-primary" />
                Upgrade to Premium
              </Button>
            </Link>
          )}
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={startTutorial}
            data-testid="start-tutorial-button"
          >
            <HelpCircle className="h-4 w-4 mr-2" />
            App Tutorial
          </Button>
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={toggleTheme}
            data-testid="theme-toggle-button"
          >
            {theme === 'dark' ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
            {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40" 
          onClick={() => setSidebarOpen(false)}
          data-testid="mobile-sidebar-overlay"
        />
      )}

      {/* Mobile Sidebar */}
      <aside 
        className={`md:hidden fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="mobile-sidebar"
      >
        {/* Header — fixed at top */}
        <div className="p-6 border-b border-border flex items-center justify-between flex-shrink-0">
          <Link to="/dashboard" className="flex items-center space-x-3" onClick={() => setSidebarOpen(false)}>
            <TestTubes className="h-6 w-6 text-primary" />
            <h1 className="text-xl font-bold">PeptideForge</h1>
          </Link>
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)} data-testid="mobile-sidebar-close">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Nav — scrollable middle region */}
        <nav
          className="flex-1 min-h-0 p-4 space-y-1 overflow-y-auto overscroll-contain"
          data-testid="mobile-sidebar-nav"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center justify-between space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </div>
                {item.premium && !isPremium && (
                  <Crown className="h-4 w-4 text-primary" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom utility buttons — pinned but in flow, not absolute */}
        <div
          className="p-4 border-t border-border space-y-2 flex-shrink-0 bg-card"
          style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
          data-testid="mobile-sidebar-footer"
        >
          {isAdmin && (
            <Link to="/admin" onClick={() => setSidebarOpen(false)}>
              <Button
                variant="outline"
                className="w-full justify-start border-amber-500/50 hover:bg-amber-500/10"
                data-testid="mobile-admin-panel-button"
              >
                <Shield className="h-4 w-4 mr-2 text-amber-400" />
                Admin Panel
              </Button>
            </Link>
          )}
          {!isPremium && (
            <Link to="/subscription" onClick={() => setSidebarOpen(false)}>
              <Button
                variant="outline"
                className="w-full justify-start border-primary/50 hover:bg-primary/10"
                data-testid="mobile-upgrade-premium-button"
              >
                <Crown className="h-4 w-4 mr-2 text-primary" />
                Upgrade to Premium
              </Button>
            </Link>
          )}
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={() => { setSidebarOpen(false); startTutorial(); }}
            data-testid="mobile-start-tutorial-button"
          >
            <HelpCircle className="h-4 w-4 mr-2" />
            App Tutorial
          </Button>
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={toggleTheme}
            data-testid="mobile-theme-toggle-button"
          >
            {theme === 'dark' ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
            {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={handleLogout}
            data-testid="mobile-logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <header className="md:hidden border-b border-border bg-card px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-button">
            <Menu className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-bold">PeptideForge</h1>
          <div className="flex items-center justify-end w-10">
            <SyncIndicator compact />
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 overflow-y-auto pb-20 md:pb-0 smooth-scroll">
          <Outlet context={{ user }} />
        </div>

        {/* Mobile Bottom Navigation */}
        <MobileNav />
      </main>
    </div>
  );
}
